/**
 * Wishlist Management System
 * Variant-aware with localStorage storage and Shopify Customer Metafield sync.
 *
 * Sync model: localStorage `wishlist` holds the current view. Every add/remove
 * is also recorded as a pending operation (`wishlist:pending`, one op per
 * handle, last one wins). On page load the SERVER list is the base and pending
 * ops are replayed on top — stale local data is never merged back, so a
 * removal can no longer be resurrected by a refresh or another device.
 * Pending ops are only cleared after the server confirms a sync; confirmed
 * ops linger as short-lived tombstones because the server can still serve a
 * pre-write snapshot for a while (read-after-write lag, late exit beacon) —
 * without the tombstone that snapshot would resurrect a removed item, or
 * silently wipe a freshly added one, and the next sync would persist either
 * mistake.
 */
(function() {
  'use strict';

  // Shopify App Proxy base path. Same-origin: Shopify signs the request and
  // forwards it (with the logged-in customer id) to the Vercel backend.
  // Set to '' to disable server sync (localStorage-only).
  const WISHLIST_API_URL = '/apps/wishlist';

  const SYNC_DEBOUNCE_MS = 600;
  const REMOVE_SYNC_DEBOUNCE_MS = 300;
  const RETRY_DELAYS_MS = [2000, 5000, 15000];
  const REMOVE_OP_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
  const TOMBSTONE_GRACE_MS = 2 * 60 * 1000; // outlives server read lag
  const MAX_PENDING_OPS = 250; // matches backend MAX_ITEMS

  class WishlistManager {
    constructor() {
      this.storageKey = 'wishlist';
      this.pendingKey = 'wishlist:pending';
      this.ownerKey = 'wishlist:owner';
      this.syncInProgress = false;
      this.syncQueued = false;
      this.syncTimeout = null;
      this.retryTimeout = null;
      this.retryCount = 0;
      this.lastExitFlush = 0;
    }

    async init() {
      this.migrateOldWishlist();
      this.ensureWishlistExists();
      this.migratePendingState();
      this.prunePending();
      this.handleOwnerChange();

      // Load from Shopify if API is configured and user is logged in
      if (WISHLIST_API_URL && this.isLoggedIn()) {
        await this.loadFromShopify();

        // Only push to the server when there are unconfirmed local changes.
        // (Syncing unconditionally would re-persist resurrected items and
        // write the metafield on every page load.)
        if (this.hasPendingOps()) {
          this.scheduleSync(1000);
        }
      }

      this.attachLifecycleListeners();

      setTimeout(() => {
        this.updateAllCounts();
        this.updateAllButtons();
      }, 100);
    }

    attachLifecycleListeners() {
      // Flush unconfirmed changes when the page is being left, so a removal
      // followed by an immediate refresh/navigation still reaches the server.
      window.addEventListener('pagehide', () => this.flushOnExit());
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          this.flushOnExit();
        }
      });

      // Another tab changed the wishlist: re-publish so counts, hearts AND
      // the wishlist page grid re-render from the new localStorage state.
      // (publishUpdate does not write storage, so this cannot loop.)
      window.addEventListener('storage', (event) => {
        if (event.key === this.storageKey) {
          this.publishUpdate();
        }
      });
    }

    getCustomerId() {
      // Check window.Shopify.customer first (from js-variables.liquid)
      if (window.Shopify && window.Shopify.customer && window.Shopify.customer.id) {
        return window.Shopify.customer.id.toString();
      }

      // Fallback to meta tag
      const metaTag = document.querySelector('meta[name="shopify-customer-id"]');
      if (metaTag) {
        const id = metaTag.getAttribute('content');
        if (id) {
          return id.toString();
        }
      }

      return null;
    }

    isLoggedIn() {
      return this.getCustomerId() !== null;
    }

    async loadFromShopify() {
      if (!WISHLIST_API_URL || !this.isLoggedIn()) return;

      try {
        // No customerId in the URL: the App Proxy injects the trusted
        // logged_in_customer_id server-side after verifying the signature.
        const response = await fetch(`${WISHLIST_API_URL}/get`, {
          headers: { 'Accept': 'application/json' }
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const result = await response.json();

        if (result.success && Array.isArray(result.wishlist)) {
          this.applyServerState(result.wishlist);
        }
      } catch (error) {
        console.error('Error loading wishlist from Shopify:', error);
      }
    }

    /**
     * Server list is the base; pending (unconfirmed) local ops are replayed
     * on top. The stale local list itself is never consulted, so items the
     * user removed cannot come back — the old union merge caused exactly that.
     */
    applyServerState(serverItems) {
      const base = new Map();

      for (const item of serverItems) {
        if (item && item.handle) {
          base.set(item.handle, {
            handle: item.handle,
            variantId: this.normalizeVariantId(item.variantId)
          });
        }
      }

      const pending = this.getPending();
      for (const [handle, op] of Object.entries(pending)) {
        if (op.op === 'remove') {
          base.delete(handle);
        } else if (op.op === 'add') {
          const existing = base.get(handle);
          base.set(handle, {
            handle: handle,
            variantId: op.variantId != null ? op.variantId : (existing ? existing.variantId : null)
          });
        }
      }

      const merged = Array.from(base.values());

      // Skip the write + UI event when nothing actually changed, so a page
      // load or pre-sync rebase doesn't trigger a needless full re-render.
      const sortByHandle = (list) => [...list].sort((a, b) => a.handle < b.handle ? -1 : 1);
      if (JSON.stringify(sortByHandle(merged)) === JSON.stringify(sortByHandle(this.getWishlist()))) {
        return;
      }

      localStorage.setItem(this.storageKey, JSON.stringify(merged));
      this.publishUpdate();
    }

    async syncToShopify() {
      if (!WISHLIST_API_URL || !this.isLoggedIn()) return;

      if (this.syncInProgress) {
        this.syncQueued = true;
        return;
      }

      this.syncInProgress = true;
      let synced = false;

      try {
        // Rebase on the latest server state first: the POST below replaces
        // the whole metafield, so without this a tab/device with a stale
        // list would clobber removals or additions made elsewhere.
        try {
          const getResponse = await fetch(`${WISHLIST_API_URL}/get`, {
            headers: { 'Accept': 'application/json' }
          });
          if (getResponse.ok) {
            const data = await getResponse.json();
            if (data.success && Array.isArray(data.wishlist)) {
              this.applyServerState(data.wishlist);
            }
          }
        } catch (rebaseError) {
          // Offline or server error: POST the local list as-is; the sync
          // itself will most likely fail too and take the retry path.
        }

        // Snapshot in the same synchronous block as the body build: the POST
        // reflects exactly these ops, so they may be cleared on success.
        const snapshot = this.getPending();

        const response = await fetch(`${WISHLIST_API_URL}/sync`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ wishlist: this.getWishlist() }),
          keepalive: true
        });

        const result = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(result.error || `HTTP ${response.status}: ${response.statusText}`);
        }
        // The proxy answers 200 + loggedIn:false when it did not see the
        // customer session, and then saves nothing. Treating that as a
        // confirmation would expire the ops and let the old list come back.
        if (result.loggedIn === false) {
          throw new Error('Sync not saved: proxy did not see a logged-in customer');
        }

        this.clearConfirmedOps(snapshot);
        this.retryCount = 0;
        synced = true;
      } catch (error) {
        console.error('Error syncing wishlist to Shopify:', error);
        this.scheduleRetry();
      } finally {
        this.syncInProgress = false;
        if (this.syncQueued) {
          this.syncQueued = false;
          // Only chase the queue after a success; after a failure the armed
          // retry timer owns the next attempt (the retry POSTs the full
          // list, so queued changes ride along anyway).
          if (synced) {
            this.syncToShopify();
          }
        }
      }
    }

    scheduleSync(delayMs) {
      if (!WISHLIST_API_URL || !this.isLoggedIn()) return;
      clearTimeout(this.syncTimeout);
      clearTimeout(this.retryTimeout); // the upcoming sync supersedes any armed retry
      this.syncTimeout = setTimeout(() => this.syncToShopify(), delayMs);
    }

    scheduleRetry() {
      if (this.retryCount >= RETRY_DELAYS_MS.length) return;
      const delay = RETRY_DELAYS_MS[this.retryCount];
      this.retryCount += 1;
      clearTimeout(this.retryTimeout);
      this.retryTimeout = setTimeout(() => this.syncToShopify(), delay);
    }

    /**
     * Fire-and-forget keepalive sync on page exit. Pending ops are NOT
     * cleared here: if the request lands, the next load replays them over an
     * already-correct server list (idempotent); if not, they replay and the
     * init sync persists them.
     */
    flushOnExit() {
      if (!WISHLIST_API_URL || !this.isLoggedIn() || !this.hasPendingOps()) return;
      if (Date.now() - this.lastExitFlush < 1000) return;
      this.lastExitFlush = Date.now();

      const body = JSON.stringify({ wishlist: this.getWishlist() });
      // sendBeacon first: it is the API built to survive unload. A fetch
      // without working keepalive would be silently cancelled (browsers
      // ignore unsupported RequestInit members rather than throwing).
      let sent = false;
      if (navigator.sendBeacon) {
        try {
          sent = navigator.sendBeacon(
            `${WISHLIST_API_URL}/sync`,
            new Blob([body], { type: 'application/json' })
          );
        } catch (error) {
          sent = false;
        }
      }
      if (!sent) {
        try {
          fetch(`${WISHLIST_API_URL}/sync`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: body,
            keepalive: true
          }).catch(() => {});
        } catch (error) {
          // Nothing else we can do during unload; ops replay on next load.
        }
      }
    }

    getPending() {
      try {
        const parsed = JSON.parse(localStorage.getItem(this.pendingKey));
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
      } catch (e) {
        return {};
      }
    }

    setPending(pending) {
      localStorage.setItem(this.pendingKey, JSON.stringify(pending));
    }

    hasPendingOps() {
      // Confirmed remove-tombstones don't count: they're already on the
      // server and must not trigger syncs on every page load/exit.
      return Object.values(this.getPending()).some(op => op && !op.confirmedAt);
    }

    recordOp(op, handle, variantId = null) {
      const pending = this.getPending();
      pending[handle] = { op: op, variantId: variantId, ts: Date.now() };

      const handles = Object.keys(pending);
      if (handles.length > MAX_PENDING_OPS) {
        // Evict only add-ops: an evicted remove would resurrect a deleted
        // item; removes are already bounded by REMOVE_OP_TTL_MS instead.
        // Beyond 250 the backend caps the list anyway, so old adds are the
        // least harmful thing to drop.
        handles
          .filter(h => pending[h].op === 'add')
          .sort((a, b) => (pending[a].ts || 0) - (pending[b].ts || 0))
          .slice(0, Math.max(0, handles.length - MAX_PENDING_OPS))
          .forEach(h => delete pending[h]);
      }

      this.setPending(pending);
      this.retryCount = 0; // a new user action re-arms sync retries
    }

    prunePending() {
      const pending = this.getPending();
      let changed = false;

      for (const [handle, op] of Object.entries(pending)) {
        const invalid = !op || (op.op !== 'add' && op.op !== 'remove');
        // Of the UNCONFIRMED ops only removes expire: a stale remove is the
        // only op that could delete an item freshly added on another device.
        // Adds are kept so a long-lived guest list still survives a future
        // login. Confirmed tombstones (either kind) expire much faster — they
        // only need to outlive the window in which the server can still
        // return a pre-write snapshot.
        const expired =
          !invalid &&
          ((op.op === 'remove' && Date.now() - (op.ts || 0) > REMOVE_OP_TTL_MS) ||
            (op.confirmedAt && Date.now() - op.confirmedAt > TOMBSTONE_GRACE_MS));
        if (invalid || expired) {
          delete pending[handle];
          changed = true;
        }
      }

      if (changed) this.setPending(pending);
    }

    clearConfirmedOps(snapshot) {
      const pending = this.getPending();
      let changed = false;

      for (const [handle, op] of Object.entries(snapshot)) {
        // Only clear ops unchanged since the snapshot: an op that arrived
        // mid-flight has a newer ts and must survive for the next sync.
        if (pending[handle] && pending[handle].ts === op.ts && pending[handle].op === op.op) {
          // Keep confirmed ops as tombstones for a grace period: the next
          // rebase GET may still return the pre-write server state, which
          // would otherwise resurrect a removed item (the ping-pong the
          // customer filmed in July) or wipe a freshly added one (the
          // vanishing hearts filmed in August) — and the sync after that
          // would persist the mistake. applyServerState keeps replaying
          // them meanwhile; prunePending expires them.
          if (!pending[handle].confirmedAt) {
            pending[handle].confirmedAt = Date.now();
            changed = true;
          }
        }
      }

      if (changed) this.setPending(pending);
    }

    normalizeVariantId(value) {
      if (value == null || value === '') return null;
      const num = Number(value);
      return Number.isFinite(num) ? num : null;
    }

    migrateOldWishlist() {
      const oldWishlist = localStorage.getItem(this.storageKey);
      if (oldWishlist) {
        try {
          const parsed = JSON.parse(oldWishlist);
          if (Array.isArray(parsed) && parsed.length > 0 && typeof parsed[0] === 'string') {
            const newWishlist = parsed.map(handle => ({ handle, variantId: null }));
            localStorage.setItem(this.storageKey, JSON.stringify(newWishlist));
          }
        } catch (e) {
          // Migration error - ignore
        }
      }
    }

    /**
     * One-time upgrade for browsers that predate the pending-ops model.
     * All existing local items become pending adds so the server-base merge
     * cannot silently discard them — for guests they survive a future login,
     * for logged-in users they survive a stale/empty server metafield.
     */
    migratePendingState() {
      if (localStorage.getItem(this.pendingKey) !== null) return;

      const pending = {};
      for (const item of this.getWishlist()) {
        if (item && item.handle) {
          pending[item.handle] = {
            op: 'add',
            variantId: this.normalizeVariantId(item.variantId),
            ts: Date.now()
          };
        }
      }
      this.setPending(pending);
    }

    handleOwnerChange() {
      const customerId = this.getCustomerId();
      const owner = localStorage.getItem(this.ownerKey);

      if (!customerId) {
        // A customer logged out on this browser: clear their list right away
        // so it can't linger for the next visitor, and drop the owner key so
        // wishlist items a NEW guest builds up afterwards merge normally
        // into whichever account logs in next.
        if (owner) {
          localStorage.setItem(this.storageKey, JSON.stringify([]));
          this.setPending({});
          localStorage.removeItem(this.ownerKey);
          this.publishUpdate();
        }
        return;
      }

      if (owner && owner !== customerId) {
        // Direct account switch without an intermediate guest page load:
        // don't leak the previous customer's list into this account.
        localStorage.setItem(this.storageKey, JSON.stringify([]));
        this.setPending({});
      }
      localStorage.setItem(this.ownerKey, customerId);
    }

    ensureWishlistExists() {
      if (!localStorage.getItem(this.storageKey)) {
        localStorage.setItem(this.storageKey, JSON.stringify([]));
      }
    }

    getWishlist() {
      try {
        return JSON.parse(localStorage.getItem(this.storageKey)) || [];
      } catch (e) {
        return [];
      }
    }

    saveWishlist(wishlist, syncDelayMs = SYNC_DEBOUNCE_MS) {
      localStorage.setItem(this.storageKey, JSON.stringify(wishlist));
      this.publishUpdate();
      this.scheduleSync(syncDelayMs);
    }

    addItem(productHandle, variantId = null) {
      const normalizedVariantId = this.normalizeVariantId(variantId);
      const wishlist = this.getWishlist();
      const existingIndex = wishlist.findIndex(item => item.handle === productHandle);

      if (existingIndex === -1) {
        wishlist.push({ handle: productHandle, variantId: normalizedVariantId });
        this.recordOp('add', productHandle, normalizedVariantId);
        this.saveWishlist(wishlist);
        return true;
      } else if (normalizedVariantId != null && wishlist[existingIndex].variantId !== normalizedVariantId) {
        wishlist[existingIndex].variantId = normalizedVariantId;
        this.recordOp('add', productHandle, normalizedVariantId);
        this.saveWishlist(wishlist);
        return true;
      }
      return false;
    }

    removeItem(productHandle) {
      const wishlist = this.getWishlist();
      const index = wishlist.findIndex(item => item.handle === productHandle);
      if (index > -1) {
        wishlist.splice(index, 1);
        this.recordOp('remove', productHandle);
        this.saveWishlist(wishlist, REMOVE_SYNC_DEBOUNCE_MS);
        return true;
      }
      return false;
    }

    /**
     * Update a stored handle in place (self-heal after a product's handle
     * changed and Shopify redirected the old one). Recorded as remove(old) +
     * add(new) so the change also reaches the server metafield.
     */
    renameItem(oldHandle, newHandle, options = {}) {
      if (!newHandle || oldHandle === newHandle) return false;

      const wishlist = this.getWishlist();
      const oldIndex = wishlist.findIndex(item => item.handle === oldHandle);
      if (oldIndex === -1) return false;

      const existingNew = wishlist.find(item => item.handle === newHandle);
      const variantId = existingNew ? existingNew.variantId : wishlist[oldIndex].variantId;

      if (existingNew) {
        wishlist.splice(oldIndex, 1); // already present under the new handle: dedupe
      } else {
        wishlist[oldIndex] = { handle: newHandle, variantId: variantId };
      }

      this.recordOp('remove', oldHandle);
      this.recordOp('add', newHandle, variantId);

      localStorage.setItem(this.storageKey, JSON.stringify(wishlist));
      if (!options.silent) {
        this.publishUpdate();
      }
      this.scheduleSync(SYNC_DEBOUNCE_MS);
      return true;
    }

    hasItem(productHandle) {
      return this.getWishlist().some(item => item.handle === productHandle);
    }

    getItem(productHandle) {
      return this.getWishlist().find(item => item.handle === productHandle) || null;
    }

    getCount() {
      return this.getWishlist().length;
    }

    getProductHandles() {
      return this.getWishlist().map(item => item.handle);
    }

    clear() {
      for (const item of this.getWishlist()) {
        if (item && item.handle) {
          this.recordOp('remove', item.handle);
        }
      }
      this.saveWishlist([], REMOVE_SYNC_DEBOUNCE_MS);
    }

    publishUpdate() {
      const event = new CustomEvent('wishlist:updated', {
        detail: {
          wishlist: this.getWishlist(),
          count: this.getCount()
        }
      });
      document.dispatchEvent(event);

      setTimeout(() => {
        this.updateAllCounts();
        this.updateAllButtons();
      }, 50);
    }

    updateAllCounts() {
      const counts = document.querySelectorAll('wishlist-count');
      counts.forEach(count => {
        if (count.update && typeof count.update === 'function') {
          count.update();
        }
      });
    }

    updateAllButtons() {
      const buttons = document.querySelectorAll('wishlist-button');
      buttons.forEach(button => {
        if (button.update && typeof button.update === 'function') {
          button.update();
        }
      });
    }

    // Manual sync function for debugging
    async manualSync() {
      await this.syncToShopify();
    }
  }

  // Expose manual sync to window for debugging
  window.manualWishlistSync = function() {
    if (window.WishlistManager) {
      window.WishlistManager.manualSync();
    }
  };

  /**
   * Wishlist Count Web Component
   * Displays the number of items in the wishlist
   */
  class WishlistCount extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.update();
      document.addEventListener('wishlist:updated', () => this.update());
    }

    update() {
      if (!window.WishlistManager) return;

      const count = window.WishlistManager.getCount();

      if (count === 0) {
        this.hidden = true;
        return;
      }

      this.hidden = false;

      if (count > 99) {
        this.innerHTML = '<span class="text-sm">99+</span>';
        this.classList.add('wishlist-count--small');
      } else {
        this.textContent = count;
        this.classList.remove('wishlist-count--small');
      }
    }
  }

  /**
   * Wishlist Button Web Component
   * Toggle button for adding/removing products from wishlist
   */
  class WishlistButton extends HTMLElement {
    constructor() {
      super();
      this.hasRendered = false;
    }

    connectedCallback() {
      this.productHandle = this.dataset.productHandle;
      this.variantId = this.dataset.variantId || null;

      if (!this.productHandle) {
        return;
      }

      this.render();
      this.attachEventListeners();
      setTimeout(() => this.update(), 50);
    }

    attachEventListeners() {
      this.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.toggle();
      });

      this.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.toggle();
        }
      });

      document.addEventListener('wishlist:updated', () => this.update());

      if (this.classList.contains('product-page-wishlist-button')) {
        this.observeVariantChanges();
      }
    }

    observeVariantChanges() {
      const variantInput = document.querySelector('.product-variant-id');
      if (variantInput) {
        const observer = new MutationObserver(() => {
          this.variantId = variantInput.value;
        });

        observer.observe(variantInput, { attributes: true, attributeFilter: ['value'] });

        variantInput.addEventListener('change', () => {
          this.variantId = variantInput.value;
        });
      }
    }

    render() {
      if (this.hasRendered) return;

      this.classList.add('wishlist-button');
      this.setAttribute('role', 'button');
      this.setAttribute('tabindex', '0');
      this.setAttribute('aria-label', 'Toevoegen aan wishlist');

      this.innerHTML = `
        <svg class="wishlist-button__icon" width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path class="wishlist-button__icon-outline" d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" fill="none" stroke="currentColor" stroke-width="2"/>
          <path class="wishlist-button__icon-filled" d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" fill="var(--wishlist-active-color, #7D3F5E)"/>
        </svg>
      `;

      this.hasRendered = true;
    }

    toggle() {
      if (!window.WishlistManager) return;

      const isInWishlist = window.WishlistManager.hasItem(this.productHandle);

      if (isInWishlist) {
        window.WishlistManager.removeItem(this.productHandle);
      } else {
        const currentVariantId = this.getCurrentVariantId();
        window.WishlistManager.addItem(this.productHandle, currentVariantId);

        this.classList.add('wishlist-button--animating');
        setTimeout(() => {
          this.classList.remove('wishlist-button--animating');
        }, 600);
      }
    }

    getCurrentVariantId() {
      if (this.variantId) {
        return this.variantId;
      }

      const variantInput = document.querySelector('.product-variant-id');
      if (variantInput) {
        return variantInput.value;
      }

      return null;
    }

    update() {
      if (!window.WishlistManager) return;

      const isInWishlist = window.WishlistManager.hasItem(this.productHandle);

      if (isInWishlist) {
        this.classList.add('wishlist-button--active');
        this.setAttribute('aria-label', 'Verwijderen uit wishlist');
      } else {
        this.classList.remove('wishlist-button--active');
        this.setAttribute('aria-label', 'Toevoegen aan wishlist');
      }
    }
  }

  // Register custom elements
  if (!customElements.get('wishlist-count')) {
    customElements.define('wishlist-count', WishlistCount);
  }

  if (!customElements.get('wishlist-button')) {
    customElements.define('wishlist-button', WishlistButton);
  }

  // Initialize wishlist when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWishlist);
  } else {
    initWishlist();
  }

  async function initWishlist() {
    window.WishlistManager = new WishlistManager();
    await window.WishlistManager.init();
  }
})();
